import { Fade, Modal } from "@mui/material";
import { useEffect, useMemo, useReducer } from "react";
import {
  BulkUploadModal,
  MODAL_FADE_MS,
} from "@/components/campaignBuilder/recipients/BulkUploadModal";
import type { Recipient } from "@/interfaces/CampaignBuilder";
import type { CampaignContactsResponse } from "@/interfaces/campaignContacts";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import AddRecipientWizard from "./AddRecipientWizard";
import {
  bulkImportOutcome,
  bulkImportReducer,
  bulkImportReview,
  refreshBulkReview,
} from "./addRecipients-helper";
import type { BulkImportState } from "./types";

/** Nothing open and nothing imported yet. */
const IDLE: BulkImportState = { importerOpen: false, wizard: null };

/**
 * The bulk wizard as shown. While it is open its rows are re-checked against the campaign's current
 * emails, so a row another tab or admin has added since is neither priced nor sent; with none left,
 * `emptied` is the skipped sentence to toast. Closed, it keeps the stored review: the fade-out never
 * redraws with no rows, and the refetch after a successful add (every held row a duplicate by then)
 * changes nothing.
 */
function useLiveWizard(wizard: BulkImportState["wizard"], existingEmails: readonly string[]) {
  const live = useMemo(
    () => (wizard?.open ? refreshBulkReview(wizard.review, existingEmails) : null),
    [wizard, existingEmails],
  );
  if (wizard === null || live === null) return { wizard, emptied: null };
  const outcome = bulkImportOutcome(live);
  if (outcome.kind === "toast") return { wizard, emptied: outcome.message };
  return { wizard: { review: live, open: true }, emptied: null };
}

/**
 * A launched campaign's bulk upload: the importer over the contacts modal, then the bulk wizard
 * with what it imported. OneSchema calls `onImport` and then `onClose` in the same tick; the
 * reducer keeps the importer and the wizard apart, so closing the importer never cancels the
 * wizard it just opened.
 */
export function useBulkImport(i: {
  data: CampaignContactsResponse | undefined;
  existingEmails: readonly string[];
  notify: (message: string, variant: "success" | "error") => void;
}) {
  const [state, dispatch] = useReducer(bulkImportReducer, IDLE);
  const { wizard, emptied } = useLiveWizard(state.wizard, i.existingEmails);
  const { notify } = i;

  // Every held row is on the campaign now: the wizard closes and the toast says why, once.
  useEffect(() => {
    if (emptied === null) return;
    dispatch({ type: "closeWizard" });
    notify(emptied, "error");
  }, [emptied, notify]);

  function handleImport(imported: Recipient[]) {
    // Only narrows `data`: the importer's one opener, the Add recipients menu, renders once loaded.
    if (!i.data) return;
    const review = bulkImportReview(imported, i.existingEmails, {
      automation: !!i.data.automation,
      now: new Date(),
      expireDate: i.data.campaign.expireDate,
      timeZone: resolveScheduleTimeZone(i.data.campaign.scheduledTz),
    });
    const outcome = bulkImportOutcome(review);
    if (outcome.kind === "toast") notify(outcome.message, "error");
    else dispatch({ type: "imported", review });
  }

  return {
    importerOpen: state.importerOpen,
    wizard,
    openImporter: () => dispatch({ type: "openImporter" }),
    closeImporter: () => dispatch({ type: "closeImporter" }),
    handleImport,
    closeWizard: () => dispatch({ type: "closeWizard" }),
  };
}

interface BulkImportDialogsProps {
  campaignId: string;
  data: CampaignContactsResponse | undefined;
  bulk: ReturnType<typeof useBulkImport>;
  onAdded: (ids: readonly number[], message: string) => void;
}

/**
 * The importer, then the bulk wizard. The importer sits in an MUI `Modal` so it is the top modal
 * and the contacts dialog's focus trap stands down instead of pulling focus out of the OneSchema
 * iframe; `closeAfterTransition` and the `Fade` let it fade out instead of vanishing on close.
 */
export default function BulkImportDialogs(p: BulkImportDialogsProps) {
  const { data, bulk } = p;
  return (
    <>
      {/* No MUI backdrop: the importer draws its own and closes on its X and backdrop click. */}
      <Modal
        open={bulk.importerOpen}
        onClose={bulk.closeImporter}
        closeAfterTransition
        hideBackdrop
      >
        <Fade in={bulk.importerOpen} timeout={MODAL_FADE_MS}>
          <div tabIndex={-1} className="outline-none">
            <BulkUploadModal
              isOpen={bulk.importerOpen}
              onClose={bulk.closeImporter}
              onImport={bulk.handleImport}
            />
          </div>
        </Fade>
      </Modal>
      {data && bulk.wizard && (
        <AddRecipientWizard
          open={bulk.wizard.open}
          onClose={bulk.closeWizard}
          campaignId={p.campaignId}
          envelope={data}
          entry="bulk"
          initialRows={bulk.wizard.review.rows}
          importReview={bulk.wizard.review}
          onAdded={(result, message) => p.onAdded(result.contactIds, message)}
        />
      )}
    </>
  );
}
